import { Link } from "wouter";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="page-privacy">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link href="/">
          <a className="inline-flex items-center gap-2 mb-8" data-testid="link-home">
            <img src={logoBlack} alt="Tork" className="h-10 block dark:hidden" />
            <img src={logoWhite} alt="Tork" className="h-10 hidden dark:block" />
          </a>
        </Link>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="text-privacy-title">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Effective May 28, 2026</p>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-6 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold">1. What We Collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account info</strong> — name, email, phone (if provided), business details.</li>
              <li><strong>Google profile data</strong> — only what's needed to manage your Google Business Profile: business info, reviews, posts, performance metrics.</li>
              <li><strong>Billing info</strong> — handled by our payment processor (Whop); we do not store full card numbers.</li>
              <li><strong>Usage data</strong> — pages visited, features used, errors encountered, captured via PostHog analytics.</li>
              <li><strong>Generated content</strong> — posts, replies, images, and other content you create or that the AI generates on your behalf.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">2. How We Use It</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To operate the Service — manage your listings, post on your behalf, track ranks, generate reports.</li>
              <li>To bill you and process payments.</li>
              <li>To improve the product, debug issues, and measure performance.</li>
              <li>To contact you about your account, security, and important product updates.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">3. Google User Data</h2>
            <p>
              Tork's use of information received from Google APIs adheres to the
              <a className="text-primary hover:underline" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer"> Google API Services User Data Policy</a>,
              including the Limited Use requirements. We use your Google data only to provide the features
              you've connected (managing your Google Business Profile). We do not use it for advertising
              and we do not sell it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">4. Who We Share With</h2>
            <p>We share data only with service providers needed to run the platform:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Google (Business Profile API)</li>
              <li>Whop (billing)</li>
              <li>Anthropic, OpenAI, and Vercel AI Gateway (AI content generation)</li>
              <li>Local Falcon &amp; SerpAPI (rank-tracking data sources)</li>
              <li>PostHog (analytics &amp; product telemetry)</li>
              <li>Replit Object Storage (image hosting)</li>
            </ul>
            <p>We do not sell your data to third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">5. Cookies &amp; Tracking</h2>
            <p>
              We use session cookies to keep you signed in and PostHog cookies to measure product usage and
              session replays for support and debugging. You can disable cookies in your browser, but the
              Service will not function correctly without session cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">6. Data Retention</h2>
            <p>
              We keep your data for as long as your account is active and for a reasonable period afterward
              for backups, legal compliance, and dispute resolution. You may request deletion at any time by
              contacting support.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">7. Security</h2>
            <p>
              We use industry-standard safeguards — encryption in transit, hashed passwords, scoped OAuth
              tokens — to protect your data. No system is 100% secure; you use the Service at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">8. Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the right to access, correct, export, or delete
              your personal data. Email support through your account to make a request.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">9. Children</h2>
            <p>The Service is not directed at children under 18 and we do not knowingly collect data from them.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">10. Changes</h2>
            <p>
              Material changes to this Policy will be communicated by prompting you to re-accept the
              updated Terms of Service the next time you sign in.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-border text-sm text-muted-foreground">
          <Link href="/terms"><a className="hover:underline" data-testid="link-terms">Terms of Service</a></Link>
          {" · "}
          <Link href="/"><a className="hover:underline" data-testid="link-home-bottom">Home</a></Link>
        </div>
      </div>
    </div>
  );
}
